/** VISTOERA_VIRTUAL_IP_POSITIONING_STEP1_V1 — "answered"/"required
 * satisfied" predicates, generic over the questionnaire's own
 * `question.type` (never one hardcoded check per question key, spec §9).
 * Mirrors `app.saas.services.virtual_ip_service`'s Python predicates
 * exactly (kept in sync deliberately, not shared, since one side is
 * Python and the other TypeScript) so the client-side "已完成 X / 9" /
 * pre-submit validation never disagrees with the backend's own
 * authoritative validation on submit. */

import type { VirtualIpAnswers, VirtualIpQuestion, VirtualIpQuestionType } from '../../api'

export function isFieldFilled(type: VirtualIpQuestionType, value: unknown): boolean {
  if (type === 'multi_select') return Array.isArray(value) && value.length > 0
  if (type === 'single_select' || type === 'text' || type === 'textarea') {
    return typeof value === 'string' && value.trim() !== ''
  }
  if (type === 'repeatable_group') return Array.isArray(value) && value.length > 0
  return Boolean(value)
}

export function isQuestionAnswered(question: VirtualIpQuestion, answers: VirtualIpAnswers): boolean {
  const value = answers[question.key]
  if (question.type === 'composite') {
    const sub = (value && typeof value === 'object' ? (value as Record<string, unknown>) : {}) as Record<
      string,
      unknown
    >
    return (question.fields ?? []).some((field) => isFieldFilled(field.type, sub[field.key]))
  }
  return isFieldFilled(question.type, value)
}

export function isQuestionRequiredSatisfied(question: VirtualIpQuestion, answers: VirtualIpAnswers): boolean {
  if (!question.required) return true
  const value = answers[question.key]
  if (question.type === 'composite') {
    const sub = (value && typeof value === 'object' ? (value as Record<string, unknown>) : {}) as Record<
      string,
      unknown
    >
    return (question.fields ?? [])
      .filter((field) => field.required)
      .every((field) => isFieldFilled(field.type, sub[field.key]))
  }
  return isFieldFilled(question.type, value)
}

export function answeredCount(questions: VirtualIpQuestion[], answers: VirtualIpAnswers): number {
  return questions.reduce((count, q) => count + (isQuestionAnswered(q, answers) ? 1 : 0), 0)
}

export function missingRequiredQuestions(questions: VirtualIpQuestion[], answers: VirtualIpAnswers): VirtualIpQuestion[] {
  return questions.filter((q) => q.required && !isQuestionRequiredSatisfied(q, answers))
}
